/**
 * Cross-platform share helper.
 *
 * Strategy (best → fallback):
 *   1. Web Share API with PNG file attached  — mobile native share sheet.
 *   2. Web Share API text-only               — some desktop browsers.
 *   3. Clipboard `writeText`                 — universal fallback.
 *
 * Returns "shared" | "copied" | "failed" so the caller can show the right
 * toast. AbortError (user dismissed the system share sheet) is reported as
 * "failed" without a toast — the user already knows what happened.
 */
const SHARE_URL = "https://www.curvle.world";
const SHARE_TITLE = "Curvle";

export interface ShareInput {
  /** PNG of the share card. Pass null to skip the file-share branch. */
  blob: Blob | null;
  /** Plain-text summary used as caption / clipboard payload. */
  text: string;
  /** Filename used when attaching the PNG to a share. */
  filename: string;
  /** Override URL for navigator.share / clipboard payload. When the user
   *  has just submitted an attempt, this is the challenge URL
   *  (https://www.curvle.world/c/<id>) so recipients land on the voyeur
   *  replay viewer rather than the home page. Defaults to the home page. */
  url?: string;
  /** Override title for the OS share-sheet header. Personalized like
   *  "Balarama scored 840 — beat me on Curvle". Defaults to "Curvle". */
  title?: string;
}

export type ShareStatus = "shared" | "copied" | "failed";

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}

// Verbose tracing to diagnose share-path failures in production. Each
// branch logs prefix [curvle.share] so the user can paste DevTools output.
// Set window.__curvleSilentShare = true in console to suppress.
function dlog(...args: unknown[]): void {
  if (typeof window !== "undefined" && (window as unknown as { __curvleSilentShare?: boolean }).__curvleSilentShare) return;
  // eslint-disable-next-line no-console
  console.log("[curvle.share]", ...args);
}

/**
 * On touch devices (mobile/tablet), the OS share sheet handles file
 * attachments smoothly — iOS and Android both surface the video to
 * Messages/WhatsApp/Instagram/etc. as expected.
 *
 * On desktop (Windows/macOS/Linux), Web Share with file attachments is
 * inconsistent: Chrome on Windows often returns canShare:true but then
 * the OS Share UI either fails to open or silently rejects video files.
 * Text + URL share, however, is rock-solid on desktop and is exactly
 * what we need — the URL (curvle.world/c/<id>) carries the viral hook.
 *
 * So: try file-share only on touch devices; go straight to text+URL
 * share on desktop. The video is a bonus when we can attach it; the
 * URL is the engine.
 */
function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const mq = window.matchMedia?.("(pointer: coarse)");
    if (mq && mq.matches) return true;
  } catch { /* noop */ }
  // navigator.userAgentData is available on Chromium-based browsers and
  // is the canonical way to detect mobile.
  const uad = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uad && uad.mobile === true) return true;
  // Last-resort UA sniff (only used if neither of the above is conclusive).
  return /android|iphone|ipad|ipod|mobile|touch/i.test(navigator.userAgent);
}

export async function shareResult(input: ShareInput): Promise<ShareStatus> {
  const touch = isTouchDevice();
  dlog("start", {
    touch,
    hasBlob: !!input.blob,
    blobType: input.blob?.type,
    blobSize: input.blob?.size,
    textLength: input.text.length,
    filename: input.filename,
    hasNavShare: typeof navigator.share === "function",
    hasNavCanShare: typeof navigator.canShare === "function",
  });

  const shareUrl = input.url || SHARE_URL;
  const shareTitle = input.title || SHARE_TITLE;
  dlog("using share url", shareUrl, "title", shareTitle);

  // 1) File share via Web Share API — touch devices only.
  if (
    touch &&
    input.blob &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function"
  ) {
    try {
      const file = new File([input.blob], input.filename, { type: input.blob.type || "image/png" });
      const canShareFiles = navigator.canShare({ files: [file] });
      dlog("file branch: canShare files", canShareFiles);
      if (canShareFiles) {
        await navigator.share({
          files: [file],
          text: input.text,
          url: shareUrl,
          title: shareTitle,
        });
        dlog("file branch: navigator.share resolved → shared");
        return "shared";
      }
    } catch (err) {
      const e = err as Error;
      dlog("file branch: error", e.name, e.message);
      if (isAbort(err)) return "failed";
      // fall through to text branch
    }
  }

  // 2) Text + URL Web Share API — primary on desktop, secondary on mobile.
  if (typeof navigator.share === "function") {
    try {
      dlog("text branch: calling navigator.share text-only");
      await navigator.share({ text: input.text, url: shareUrl, title: shareTitle });
      dlog("text branch: resolved → shared");
      return "shared";
    } catch (err) {
      const e = err as Error;
      dlog("text branch: error", e.name, e.message);
      if (isAbort(err)) return "failed";
      // fall through
    }
  }

  // 3) Clipboard fallback — last resort.
  try {
    dlog("clipboard branch: writing text");
    await navigator.clipboard.writeText(`${input.text} ${shareUrl}`);
    dlog("clipboard branch: resolved → copied");
    return "copied";
  } catch (err) {
    const e = err as Error;
    dlog("clipboard branch: error", e.name, e.message);
    return "failed";
  }
}
